import { useRouter } from "next/router";

import { DotsVerticalIcon, ReplyIcon } from "@enkor/design/icons";
import { grayScale } from "@enkor/design/colors";
import { ToastError, ToastSuccess } from "@logics/utils/toast";
import { linkify } from "@logics/utils/urlHandler";
import Avatar from "@domains/common/views/components/Avatar/Avatar";
import useModalState from "@logics/hooks/useModalState";
import { useAuthSession } from "@domains/auth/hooks/useAuthSession";
import { ERROR_MESSAGE } from "@domains/common/constants/toastMessages";
import { useAttachLinkClickHandler } from "@logics/hooks/useAttachLinkClickHandler";
import { useCreateReplyMutation } from "@domains/project/network/projectMutations";
import { COMMENT_MESSAGE } from "@domains/project/constants/toastMessages";
import { useCommentMenu } from "@domains/project/hooks/useCommentMenu";
import WebViewBridge from "@logics/utils/webViewBridge";
import { NAVIGATION_ROUTE } from "@domains/common/constants/navigationRoute";

import CommentForm from "../CommentForm/CommentForm";

import * as S from "./CommentCard.style";

interface CommentCardProps {
  isMySelf: boolean;
  projectId?: string;
  commentId?: number;
  authorName: string;
  authorId: number;
  authorProfile?: string;
  commentPayload: string;
  commentedAt: string;
}

const CommentCard = ({
  isMySelf,
  projectId,
  commentId,
  authorName = "the commenter",
  authorId,
  authorProfile,
  commentPayload,
  commentedAt,
}: CommentCardProps) => {
  const { push } = useRouter();

  const { openModal } = useModalState();
  const { guardRoute } = useAuthSession();

  const { handleClickMenu } = useCommentMenu({
    isMySelf,
    projectId,
    commentId,
    payload: commentPayload,
  });

  const replyCreateMutation = useCreateReplyMutation();

  useAttachLinkClickHandler({ id: commentId || "" });

  // CREATE REPLY FOR THIS COMMENT
  const handleOpenReplyForm = () =>
    guardRoute(() => {
      const handleSubmitReply = async (content: string) => {
        if (!projectId || !commentId) return;

        try {
          await replyCreateMutation.mutateAsync({
            projectId,
            commentId,
            content,
          });
          ToastSuccess(COMMENT_MESSAGE.REPLY_ADDED);
        } catch (error) {
          ToastError(ERROR_MESSAGE.SERVER_ERROR);
        }
      };

      openModal({
        CustomComponent: (
          <CommentForm
            headerTitle={`Replying to ${authorName}`}
            onSubmitComment={handleSubmitReply}
          />
        ),
      });
    });

  const moveToUserProfilePage = (userId: number) => {
    if (!userId) return;

    if (WebViewBridge.isInAppWebView()) {
      WebViewBridge.getInstance().navigateToUserProfile(userId);
    } else {
      push({
        pathname: `${NAVIGATION_ROUTE.PROFILE.HREF}/${userId}`,
        query: NAVIGATION_ROUTE.PROFILE.QUERY,
      });
    }
  };

  return (
    <S.Container>
      <Avatar
        profile={{ url: authorProfile, id: authorId }}
        size="xl"
        style={{ cursor: "pointer" }}
        onClick={() => moveToUserProfilePage(authorId)}
      />
      <S.ContentWrapper>
        <S.MenuIconWrapper
          $shouldShow
          $size={16}
          role="button"
          onClick={handleClickMenu}
        >
          <DotsVerticalIcon size={16} strokeColor={grayScale.white} />
        </S.MenuIconWrapper>

        <S.WriterAndTime>
          <S.Writer
            name="s"
            as="button"
            onClick={() => moveToUserProfilePage(authorId)}
          >
            {authorName}
          </S.Writer>
          <S.Time name="s">{commentedAt}</S.Time>
        </S.WriterAndTime>

        <S.CommentContent
          name="m"
          dangerouslySetInnerHTML={{
            __html: linkify(commentPayload, String(commentId)),
          }}
        />

        <S.ReplyButton onClick={handleOpenReplyForm}>
          <S.ReplyIconWrapper $size={20}>
            <ReplyIcon size={20} strokeColor={grayScale.white} />
          </S.ReplyIconWrapper>
          <span>Reply</span>
        </S.ReplyButton>
      </S.ContentWrapper>
    </S.Container>
  );
};

export default CommentCard;
